import React, { useState, useEffect } from "react";
import { ShoppingCart, Search, RefreshCw, Package, CreditCard, Truck, Filter, X, LayoutGrid, List, Calendar } from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { User } from "./types";
import * as apiConfig from "../../utils/apiConfig";

interface WebOrdersProps {
  currentUser?: User;
}

interface Address {
  address1: string;
  address2: string;
  city: string;
  postcode: string;
  zone: string;
  country: string;
}

interface Customer {
  name: string;
  email: string;
  phone: string;
  billingAddress: Address;
  shippingAddress: Address | null;
  addressesAreSame: boolean;
}

interface PaidItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface WebOrder {
  orderId: number;
  originalOrderId: number;
  dateAdded: string | { $date: string };
  orderStatus: string;
  customer: Customer;
  paidItems: PaidItem[];
  paymentMethod: string;
  paymentStatus: string;
  codFee: number;
  productsCost: number;
  shippingMethod: string;
  shippingCost: number;
  shippingLabel: string;
  totalCost: number;
  trackingNumber: string | null;
  currency: string;
}

const WebOrders: React.FC<WebOrdersProps> = ({ currentUser }) => {
  const [orders, setOrders] = useState<WebOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 50;

  // Search and filter states
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("today");
  
  // Responsive view mode: table for desktop, cards for mobile
  const getInitialViewMode = (): "table" | "cards" => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 768 ? "table" : "cards";
    }
    return "cards";
  };
  const [viewMode, setViewMode] = useState<"table" | "cards">(getInitialViewMode);

  const API_BASE_URL = apiConfig.API_BASE_URL;

  const loadWebOrders = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", itemsPerPage.toString());

      // Add search and filter parameters
      if (orderIdSearch.trim()) {
        params.append("orderId", orderIdSearch.trim());
      }
      if (customerSearch.trim()) {
        params.append("customerSearch", customerSearch.trim());
      }
      if (paymentStatusFilter !== "all") {
        params.append("paymentStatus", paymentStatusFilter);
      }
      if (dateFilter !== "allTime") {
        params.append("dateFilter", dateFilter);
      }

      const url = `${API_BASE_URL}/api/orders/web?${params.toString()}`;
      const authToken = localStorage.getItem("authToken");
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });
      
      const data = await response.json();

      if (data.success) {
        setOrders(data.data.orders || []);
        const pagination = data.data.pagination || {};
        setTotalPages(pagination.totalPages || 1);
        setTotalCount(pagination.total || 0);
        setCurrentPage(pagination.page || page);
      } else {
        setError(data.error || "Failed to load web orders");
      }
    } catch (err) {
      console.error("Error loading web orders:", err);
      setError("Failed to load web orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebOrders(1);
  }, [paymentStatusFilter, dateFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadWebOrders(page);
  };

  const handleRefresh = () => {
    loadWebOrders(currentPage);
  };

  const handleSearchOrderId = () => {
    setCurrentPage(1);
    loadWebOrders(1);
  };

  const handleSearchCustomer = () => {
    setCurrentPage(1);
    loadWebOrders(1);
  };

  const handleClearFilters = () => {
    setOrderIdSearch("");
    setCustomerSearch("");
    setPaymentStatusFilter("all");
    setDateFilter("today");
    setCurrentPage(1);
    // Reload will be triggered by useEffect
  };

  const formatDate = (dateInput: string | { $date: string }) => {
    if (!dateInput) return "N/A";

    let date: Date;
    if (typeof dateInput === "string") {
      date = new Date(dateInput);
    } else if (typeof dateInput === "object" && "$date" in dateInput) {
      date = new Date(dateInput.$date);
    } else {
      return "N/A";
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number, currency: string = "EUR") => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm px-4 sm:px-6 py-4 mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-wrap flex-1">
            {/* Search by Order ID */}
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Order ID..."
                  value={orderIdSearch}
                  onChange={(e) => {
                    setOrderIdSearch(e.target.value);
                    if (e.target.value === "") {
                      setCurrentPage(1);
                      loadWebOrders(1);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearchOrderId();
                    }
                  }}
                  className="border border-gray-300 rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-60"
                />
                {orderIdSearch && (
                  <button
                    onClick={() => {
                      setOrderIdSearch("");
                      setCurrentPage(1);
                      loadWebOrders(1);
                    }}
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleSearchOrderId}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-1.5 rounded-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#264a3e] z-10"
                  style={{ backgroundColor: "#264a3e" }}
                  title="Search"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => setViewMode(viewMode === "table" ? "cards" : "table")}
                className="inline-flex items-center justify-center p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation flex-shrink-0"
                title={viewMode === "table" ? "Switch to card view" : "Switch to table view"}
              >
                {viewMode === "table" ? (
                  <LayoutGrid className="w-4 h-4" />
                ) : (
                  <List className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Search by Customer Info */}
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name, Email, or Phone..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    if (e.target.value === "") {
                      setCurrentPage(1);
                      loadWebOrders(1);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearchCustomer();
                    }
                  }}
                  className="border border-gray-300 rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-60"
                />
                {customerSearch && (
                  <button
                    onClick={() => {
                      setCustomerSearch("");
                      setCurrentPage(1);
                      loadWebOrders(1);
                    }}
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleSearchCustomer}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-1.5 rounded-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#264a3e] z-10"
                  style={{ backgroundColor: "#264a3e" }}
                  title="Search"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Payment Status Filter */}
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                <option value="all">All Statuses</option>
                <option value="Paid Online">Paid Online</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                <option value="allTime">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleClearFilters}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Clear all filters"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Clear</span>
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-[#264a3e]" />
            {dateFilter === "today" && !orderIdSearch && !customerSearch && paymentStatusFilter === "all"
              ? "Today's Web Orders:" 
              : "Filtered Web Orders:"}
          </span>
          <span className="text-lg font-semibold text-gray-900">{totalCount}</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      {/* Orders Display - Table or Cards */}
      {viewMode === "table" ? (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[250px]">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                  Goods (Paid)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  COD Fee
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Products Cost
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shipment
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p>No web orders found</p>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.orderId} className="hover:bg-gray-50">
                    {/* Order ID */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">#{order.orderId}</div>
                      <div className="text-xs text-gray-500">Web: {order.originalOrderId}</div>
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-4">
                      <div className="space-y-2">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{order.customer.name}</div>
                          <div className="text-xs text-gray-600">{order.customer.email}</div>
                          <div className="text-xs text-gray-600">{order.customer.phone}</div>
                        </div>
                        
                        <div className="border-t pt-2 mt-2">
                          <div className="text-xs font-medium text-gray-700 mb-1">Billing Address:</div>
                          <div className="text-xs text-gray-600">
                            {order.customer.billingAddress.address1}
                            {order.customer.billingAddress.address2 && `, ${order.customer.billingAddress.address2}`}
                            <br />
                            {order.customer.billingAddress.city}, {order.customer.billingAddress.postcode}
                            <br />
                            {order.customer.billingAddress.zone}, {order.customer.billingAddress.country}
                          </div>
                        </div>

                        {order.customer.addressesAreSame ? (
                          <div className="text-xs text-green-600 italic">
                            Shipping address same as billing
                          </div>
                        ) : order.customer.shippingAddress && (
                          <div className="border-t pt-2 mt-2">
                            <div className="text-xs font-medium text-gray-700 mb-1">Shipping Address:</div>
                            <div className="text-xs text-gray-600">
                              {order.customer.shippingAddress.address1}
                              {order.customer.shippingAddress.address2 && `, ${order.customer.shippingAddress.address2}`}
                              <br />
                              {order.customer.shippingAddress.city}, {order.customer.shippingAddress.postcode}
                              <br />
                              {order.customer.shippingAddress.zone}, {order.customer.shippingAddress.country}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Goods (Paid Items) */}
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {order.paidItems.map((item, idx) => (
                          <div key={idx} className="text-xs">
                            <div className="font-medium text-gray-900">{item.productName}</div>
                            <div className="text-gray-600">
                              Qty: {item.quantity} × {formatCurrency(item.unitPrice, order.currency)} = {formatCurrency(item.total, order.currency)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>

                    {/* Payment Status */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            order.paymentStatus === "Paid Online"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {order.paymentStatus === "Paid Online" ? (
                            <CreditCard className="w-3 h-3 mr-1" />
                          ) : (
                            <Package className="w-3 h-3 mr-1" />
                          )}
                          {order.paymentStatus}
                        </span>
                        <span className="text-xs text-gray-500">{order.paymentMethod}</span>
                      </div>
                    </td>

                    {/* COD Fee */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.codFee > 0 ? formatCurrency(order.codFee, order.currency) : "/"}
                    </td>

                    {/* Products Cost */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(order.productsCost, order.currency)}
                    </td>

                    {/* Shipment */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm">
                        <Truck className="w-3 h-3 text-gray-500" />
                        {order.shippingCost === 0 ? (
                          <span className="text-green-600 font-medium">Free Shipping</span>
                        ) : (
                          <span className="text-gray-900">{formatCurrency(order.shippingCost, order.currency)}</span>
                        )}
                      </div>
                    </td>

                    {/* Total Cost */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      {formatCurrency(order.totalCost, order.currency)}
                    </td>

                    {/* Date */}
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(order.dateAdded)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{" "}
                    <span className="font-medium">{totalPages}</span> (
                    <span className="font-medium">{totalCount}</span> total orders)
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Card View */
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No web orders found</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4">
                {orders.map((order) => (
                  <div
                    key={order.orderId}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
                  >
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-4 pb-4 border-b">
                      <div>
                        <div className="text-xl font-bold text-gray-900">#{order.orderId}</div>
                        <div className="text-sm text-gray-500 mt-1">Web Order: {order.originalOrderId}</div>
                        <div className="text-sm text-gray-500 mt-1">{formatDate(order.dateAdded)}</div>
                      </div>
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                          order.paymentStatus === "Paid Online"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {order.paymentStatus === "Paid Online" ? (
                          <CreditCard className="w-4 h-4 mr-1.5" />
                        ) : (
                          <Package className="w-4 h-4 mr-1.5" />
                        )}
                        {order.paymentStatus}
                      </span>
                    </div>

                    {/* Customer Info */}
                    <div className="mb-4 pb-4 border-b">
                      <div className="text-base font-semibold text-gray-900 mb-2">{order.customer.name}</div>
                      <div className="text-sm text-gray-600 mb-1">{order.customer.email}</div>
                      <div className="text-sm text-gray-600 mb-3">{order.customer.phone}</div>
                      
                      <div className="bg-gray-50 rounded-md p-3 mb-2">
                        <div className="font-semibold text-sm text-gray-900 mb-1.5">Billing:</div>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {order.customer.billingAddress.address1}
                          {order.customer.billingAddress.address2 && (
                            <>
                              <br />
                              {order.customer.billingAddress.address2}
                            </>
                          )}
                          <br />
                          {order.customer.billingAddress.city}, {order.customer.billingAddress.postcode}
                          <br />
                          {order.customer.billingAddress.zone}, {order.customer.billingAddress.country}
                        </div>
                      </div>
                      
                      {order.customer.addressesAreSame ? (
                        <div className="text-sm text-green-600 italic font-medium">
                          ✓ Shipping address same as billing
                        </div>
                      ) : order.customer.shippingAddress && (
                        <div className="bg-blue-50 rounded-md p-3">
                          <div className="font-semibold text-sm text-gray-900 mb-1.5">Shipping:</div>
                          <div className="text-sm text-gray-700 leading-relaxed">
                            {order.customer.shippingAddress.address1}
                            {order.customer.shippingAddress.address2 && (
                              <>
                                <br />
                                {order.customer.shippingAddress.address2}
                              </>
                            )}
                            <br />
                            {order.customer.shippingAddress.city}, {order.customer.shippingAddress.postcode}
                            <br />
                            {order.customer.shippingAddress.zone}, {order.customer.shippingAddress.country}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Products */}
                    <div className="mb-4 pb-4 border-b">
                      <div className="text-base font-semibold text-gray-900 mb-3">Products:</div>
                      <div className="space-y-3">
                        {order.paidItems.map((item, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-md p-3">
                            <div className="text-sm font-medium text-gray-900 mb-1.5 leading-relaxed">
                              {item.productName}
                            </div>
                            <div className="text-sm text-gray-600">
                              {item.quantity} × {formatCurrency(item.unitPrice, order.currency)} = <span className="font-semibold">{formatCurrency(item.total, order.currency)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Costs Summary */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">Products:</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(order.productsCost, order.currency)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">COD Fee:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {order.codFee > 0 ? formatCurrency(order.codFee, order.currency) : "—"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600 flex items-center">
                          <Truck className="w-4 h-4 text-gray-500 mr-1.5" />
                          Shipping:
                        </span>
                        <span className="text-sm font-semibold">
                          {order.shippingCost === 0 ? (
                            <span className="text-green-600">Free</span>
                          ) : (
                            <span className="text-gray-900">{formatCurrency(order.shippingCost, order.currency)}</span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 pt-3 border-t-2 border-gray-200">
                        <span className="text-base font-semibold text-gray-900">Total:</span>
                        <span className="text-lg font-bold text-[#264a3e]">{formatCurrency(order.totalCost, order.currency)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination for Card View */}
              {totalPages > 1 && (
                <div className="bg-white rounded-lg shadow-sm px-4 py-3 flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{currentPage}</span> of{" "}
                        <span className="font-medium">{totalPages}</span> (
                        <span className="font-medium">{totalCount}</span> total orders)
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WebOrders;
